import React, { useRef } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import {
  DropdownIndicator,
  Option,
} from 'components/application/CollectionSelect';
import { DatePicker } from 'components/application/DatePickerField';

import {
  permittedIntervals,
  permittedPeriods,
} from 'components/trainingRegisterReports/schemas/dateSchema';

const permittedPeriodsSet = new Set(permittedPeriods);

const integersRegex = /^\d+(\.\d{0,2})?/;

function SingleValue({ ...props }) {
  return (
    <components.SingleValue {...props}>
      <div className='tw-relative -tw-top-[1px]'>{props.children}</div>
    </components.SingleValue>
  );
}

export default function DatePeriod({
  afterProps = {},
  beforeProps = {},
  customDateRangeFromProps = {},
  customDateRangeToProps = {},
  datePeriod,
  fieldError,
  name,
  onChange,
  onDateRangeChange,
  onExactDateProps = {},
  onFocus,
  periods,
}) {
  // refs are required in order to stop first option from being focused when select is focused, a bit of a hack!!!
  const selectPeriodRef = useRef();
  const selectIntervalRef = useRef();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handlePeriodChange = (selected) => {
    onChange('period', selected ? selected.value : '');
  };

  const handleFrequencyChange = (event) => {
    const matchedValue = event.target.value.match(integersRegex);

    onChange(
      `${datePeriod.period}Frequency`,
      matchedValue ? matchedValue[0] : '',
    );
  };

  const handleIntervalChange = (selected) => {
    onChange(`${datePeriod.period}Interval`, selected ? selected.value : '');
  };

  const handleDateRangeFromChange = (_, date) => {
    const toDate =
      moment(date).isAfter(datePeriod.customDateRangeTo, 'day') ?
        null
      : undefined;

    onDateRangeChange(date, toDate);
  };

  const handleDateRangeToChange = (_, date) => {
    const fromDate =
      moment(date).isBefore(datePeriod.customDateRangeFrom, 'day') ?
        null
      : undefined;

    onDateRangeChange(fromDate, date);
  };

  const isDatePickerVisible = ['before', 'after', 'onExactDate'].includes(
    datePeriod.period,
  );

  let dateProps;
  if (datePeriod.period === 'before') {
    dateProps = beforeProps;
  } else if (datePeriod.period === 'after') {
    dateProps = afterProps;
  } else if (datePeriod.period === 'onExactDate') {
    dateProps = onExactDateProps;
  }

  const isDatePickerFromToVisible = datePeriod.period === 'customDateRange';

  const selectablePeriods =
    periods ?
      periods.filter((period) => permittedPeriodsSet.has(period))
    : permittedPeriods;

  const periodOptions = selectablePeriods.map((period) => ({
    label: i18nTShared(`periods.${period}`),
    value: period,
  }));

  const isFrequencyIntervalPickerVisible = [
    'withinTheNext',
    'withinTheLast',
  ].includes(datePeriod.period);

  const intervalOptions =
    isFrequencyIntervalPickerVisible &&
    permittedIntervals.map((internval) => ({
      label: i18nTShared(`intervals.${internval}`),
      value: internval,
    }));

  return (
    <div onFocus={onFocus}>
      <div className='tw-mb-2'>
        <Select
          autoFocus={false}
          className='collection-select__select-container'
          classNamePrefix='collection-select'
          components={{ SingleValue, DropdownIndicator, Option }}
          controlShouldRenderValue={true}
          id={`${name}_period`}
          isSearchable={false}
          name={`${name}_period`}
          onChange={handlePeriodChange}
          onFocus={() => {
            selectPeriodRef.current.select.getNextFocusedOption = () => null;
          }}
          options={periodOptions}
          ref={selectPeriodRef}
          styles={{
            menu: (base) => ({ ...base, maxHeight: 288 }),
            menuList: (base) => ({ ...base, maxHeight: 286 }),
          }}
          value={periodOptions.find(
            (periodOption) => periodOption.value === datePeriod.period,
          )}
        />
      </div>
      {isFrequencyIntervalPickerVisible && (
        <div className='tw-flex'>
          <div className='tw-mr-2 tw-w-20'>
            <input
              className={classNames(
                'field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
                fieldError?.fieldHighlighted ?
                  'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
                : 'tw-border-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
              )}
              id={`${name}_frequency`}
              name={`${name}_frequency`}
              onChange={handleFrequencyChange}
              type='text'
              value={datePeriod[`${datePeriod.period}Frequency`]}
            />
          </div>
          <div className='tw-w-full'>
            <Select
              autoFocus={false}
              className='collection-select__select-container'
              classNamePrefix='collection-select'
              components={{ SingleValue, DropdownIndicator, Option }}
              controlShouldRenderValue={true}
              id={`${name}_interval`}
              isSearchable={false}
              name={`${name}_interval`}
              onChange={handleIntervalChange}
              onFocus={() => {
                selectIntervalRef.current.select.getNextFocusedOption = () =>
                  null;
              }}
              options={intervalOptions}
              ref={selectIntervalRef}
              value={
                datePeriod[`${datePeriod.period}Interval`] && {
                  label: i18nTShared(
                    `intervals.${datePeriod[`${datePeriod.period}Interval`]}`,
                  ),
                  value: datePeriod[`${datePeriod.period}Interval`],
                }
              }
            />
          </div>
        </div>
      )}
      {isDatePickerVisible && (
        <div className='tw-w-36'>
          <DatePicker
            fieldError={fieldError}
            id={`${name}_date`}
            latestDate={dateProps.latestDate}
            name={`${name}_date`}
            onChange={(_, date) => onChange(datePeriod.period, date)}
            value={datePeriod[datePeriod.period]}
          />
        </div>
      )}
      {isDatePickerFromToVisible && (
        <div className='tw-flex'>
          <DatePicker
            fieldError={fieldError}
            id={`${name}_custom_date_range_from`}
            latestDate={customDateRangeFromProps.latestDate}
            name={`${name}_custom_date_range_from`}
            onChange={handleDateRangeFromChange}
            value={datePeriod.customDateRangeFrom}
          />
          <div className='tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-font-medium'>
            {i18nTShared('prepositions.to')}
          </div>
          <DatePicker
            fieldError={fieldError}
            id={`${name}_custom_date_range_to`}
            latestDate={customDateRangeToProps.latestDate}
            name={`${name}_custom_date_range_to`}
            onChange={handleDateRangeToChange}
            value={datePeriod.customDateRangeTo}
          />
        </div>
      )}
    </div>
  );
}

DatePeriod.propTypes = {
  name: PropTypes.string.isRequired,
  datePeriod: PropTypes.object.isRequired,
  periods: PropTypes.array,
  fieldError: PropTypes.object,
  beforeProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  afterProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  onExactDateProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  customDateRangeFromProps: PropTypes.shape({
    latestDate: PropTypes.instanceOf(Date),
  }),
  customDateRangeToProps: PropTypes.shape({
    latestDate: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};
